import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import Content from 'views/components/ui/content';
import { SearchWrapper } from 'components/calendar/calendarFiltersAndSearch/style';
import searchIcon from 'assets/img/bars-search-search-bar.svg';
import { GET_CLINIC_DT_CAPACITY } from 'api/graphql/v2/queries/Ops';
import { IClinicDtCapacityCell, IOpsClinic, IOpsClinicRow } from 'model/ops';
import {
  buildClinicRows,
  filterClinicRows,
  visibleDisciplines
} from 'utils/mappers/response/clinicDtCapacity';
import { DISCIPLINE_COLOURS } from './disciplineColours';
import { OpsHeader } from './OpsHeader';
import { OpsWrapper, TitleWrapper } from './style';

const pluralise = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const cellTitle = (cell: IClinicDtCapacityCell) => {
  const split = `${cell.therapistOpenSlots} therapist, ${cell.assistantOpenSlots} assistant`;
  if (!cell.cliniciansWithoutCareSessions) return split;
  return `${split} — ${pluralise(
    cell.cliniciansWithoutCareSessions,
    'clinician'
  )} with no care sessions on their template`;
};

const CapacityCell: React.FC<{ cell?: IClinicDtCapacityCell }> = ({ cell }) => {
  // An em dash means nobody works this discipline here — deliberately distinct
  // from a zero, which means staffed but fully booked.
  if (!cell || cell.clinicians === 0) {
    return (
      <td className="numeric" title="No clinicians in this discipline">
        <span className="no-staff" role="img" aria-label="No clinicians">
          —
        </span>
      </td>
    );
  }
  // The clinician count always shows, and a warning adds a line beneath it
  // rather than replacing it. The row height does not follow from that: every
  // cell is a fixed block sized to this three-line shape, so a cell with fewer
  // lines still occupies it — see the cell-block note in style.ts.
  return (
    <td className="numeric" title={cellTitle(cell)}>
      <span className="open-slots">{cell.openSlots}</span>
      <span className="staff-line">
        {pluralise(cell.clinicians, 'clinician')}
      </span>
      {cell.cliniciansWithoutCareSessions > 0 && (
        <span className="unconfigured">
          {cell.cliniciansWithoutCareSessions} with no care sessions
        </span>
      )}
    </td>
  );
};

export const OpsTable: React.FC<{
  clinics: IOpsClinic[];
  clinicDtCapacity: IClinicDtCapacityCell[];
}> = ({ clinics, clinicDtCapacity }) => {
  const [filter, setFilter] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Clearing hands the caret straight back, so the next search is a keystroke
  // away rather than another click into the field.
  const clearFilter = () => {
    setFilter('');
    searchRef.current?.focus();
  };

  const allRows = useMemo(() => buildClinicRows(clinics, clinicDtCapacity), [
    clinics,
    clinicDtCapacity
  ]);
  const rows = useMemo(() => filterClinicRows(allRows, filter), [
    allRows,
    filter
  ]);
  // Columns come from every clinic loaded, not from the filtered subset: the
  // table keeps one shape whatever is typed, and a clinic that staffs none of
  // the disciplines still reads as a row of em dashes under the same headers
  // rather than collapsing to a bare name.
  const disciplines = useMemo(() => visibleDisciplines(allRows), [allRows]);

  return (
    <>
      <OpsHeader />

      <TitleWrapper>
        <div className="ops-title-outer-wrap">
          <div>
            {/* Names the table, not the page — Operations above it does that —
                so it sits an h2 below, both in level and in size. */}
            <h2>Open recurring slots</h2>
            <p className="ops-subtitle">
              Care sessions on each clinician&apos;s standing template with no
              client in them. Clinician count sits beneath each figure.
            </p>
          </div>
          <div className="ops-filter">
            {/* Same search treatment as the Provider Roster, so the control is
                recognisably the app's search rather than a bespoke box. */}
            <SearchWrapper isStaffPage={true}>
              <div className="client_search_wrap provider_search_wrap">
                <div className="client-Provider_wrapper staff_wrapper waitlist_Wrapper">
                  <input
                    ref={searchRef}
                    className="rbt-input"
                    type="search"
                    value={filter}
                    placeholder="Search Clinic"
                    aria-label="Filter clinics by name"
                    autoComplete="off"
                    onChange={event => setFilter(event.target.value)}
                  />
                </div>
                {/* The × takes the magnifier's own place rather than sitting
                    beside it: one affordance in one position, so nothing in the
                    control moves as you type. */}
                {filter ? (
                  <button
                    type="button"
                    className="search-icon-waitList ops-search-clear"
                    aria-label="Clear search"
                    onClick={clearFilter}
                  >
                    ×
                  </button>
                ) : (
                  <img
                    src={searchIcon}
                    className="search-icon-waitList"
                    alt=""
                  />
                )}
              </div>
            </SearchWrapper>
            <div className="filter-meta">
              <span>
                {rows.length === allRows.length
                  ? pluralise(allRows.length, 'clinic')
                  : `${rows.length} of ${allRows.length} clinics`}
              </span>
              {!!filter && (
                <button type="button" onClick={clearFilter}>
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </TitleWrapper>

      <OpsWrapper>
        <div className="table-card">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Clinic</th>
                  {disciplines.map(discipline => (
                    <th key={discipline} scope="col" className="numeric">
                      <span className="column-head">
                        <span
                          className="swatch"
                          style={{ background: DISCIPLINE_COLOURS[discipline] }}
                        />
                        {discipline}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td
                      className="empty-state"
                      colSpan={disciplines.length + 1}
                    >
                      {filter.trim()
                        ? `No clinic matches “${filter}”.`
                        : 'No clinics to show.'}
                    </td>
                  </tr>
                )}
                {rows.map((row: IOpsClinicRow) => (
                  <tr
                    key={row.id}
                    className={row.isSpoke ? 'spoke' : undefined}
                  >
                    <td>
                      {/* Drill-down is the only way into a clinic — never
                          search — so the name itself is the control. */}
                      <Link
                        className="clinic-name"
                        to={`/ops/${row.id}`}
                        title={row.name}
                      >
                        {row.name}
                      </Link>
                    </td>
                    {disciplines.map(discipline => (
                      <CapacityCell
                        key={discipline}
                        cell={row.cells[discipline]}
                      />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </OpsWrapper>
    </>
  );
};

const OpsPage: React.FC<{}> = () => {
  const { loading, error, data } = useQuery(GET_CLINIC_DT_CAPACITY, {
    fetchPolicy: 'network-only'
  });

  return (
    <Content loading={loading} error={error} data={data}>
      {({
        clinics,
        clinicDtCapacity
      }: {
        clinics: IOpsClinic[];
        clinicDtCapacity: IClinicDtCapacityCell[];
      }) => <OpsTable clinics={clinics} clinicDtCapacity={clinicDtCapacity} />}
    </Content>
  );
};

export default OpsPage;
